/**
 * AuthProvider — root auth state provider. Lives at app/providers/ so it can
 * import from both shared/ (AuthContext) and features/auth/api/ (authApi).
 *
 * Flow:
 *   1. Mount: status='loading'. One-shot `useEffect` (StrictMode-safe via mountedRef) fires.
 *   2. POST /token/refresh → on success: setInMemoryToken + enable /me/ query.
 *   3. GET /me/ → on success: status='authenticated', user from server.
 *   4. Refresh or /me/ fails → status='unauthenticated'.
 *   5. Cross-tab sync: 'auth:logout' storage event clears token + redirects.
 *   6. Any invalidateQueries(authKeys.me()) re-fetches /me/ and updates context.
 *      Photo upload and profile update use this path — no custom events needed.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { withBase } from '@/shared/lib/basePath';
import { authKeys, settingsKeys } from '@/shared/lib/queryKeys';
import { setInMemoryToken, clearInMemoryToken, clearSessionHint, hasSessionHint } from '@/shared/lib/tokenStore';
import { getMe, refreshToken } from '@/features/auth/api/authApi';
import { AuthContext, type AuthState } from '@/shared/hooks/useAuthContext';
import { applyTheme } from '@/shared/lib/theme';
import i18n from '@/i18n';
import type { UserPreferences } from '@/shared/types/user';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const qc = useQueryClient();
  // Prevents double-fire in React StrictMode — refresh cookie rotates on each call,
  // so a second concurrent call would hit a blacklisted token and 401.
  const mountedRef = useRef(false);
  // null=still attempting refresh, true=token acquired, false=refresh failed
  const [tokenAcquired, setTokenAcquired] = useState<boolean | null>(null);

  // Silent refresh on mount — re-hydrates in-memory token from httpOnly cookie.
  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;

    // Skip the refresh probe entirely when no session was ever established in
    // this browser — avoids a wasted POST /token/refresh that 401s on the login
    // page for never-logged-in / logged-out users.
    if (!hasSessionHint()) {
      setTokenAcquired(false);
      return;
    }

    refreshToken()
      .then(({ access }) => {
        setInMemoryToken(access);
        setTokenAcquired(true);
      })
      .catch(() => {
        // Stale hint (cookie expired/blacklisted) — clear it so the next load
        // doesn't probe again.
        clearSessionHint();
        setTokenAcquired(false);
      });
  }, []);

  // Drive user from /me/ so any invalidation (photo upload, profile update) propagates here.
  const meQuery = useQuery({
    queryKey: authKeys.me(),
    queryFn: getMe,
    enabled: tokenAcquired === true,
    staleTime: 1000 * 60 * 5,
    retry: false,
  });

  // Seed settings from /me/ and apply theme + language immediately so preferences
  // take effect the moment the user authenticates, not only when SettingsPage mounts.
  useEffect(() => {
    const prefs = meQuery.data?.preferences;
    if (!prefs) return;
    qc.setQueryData<UserPreferences>(settingsKeys.preferences(), prefs);
    // No crossfade on auth — avoids a flash during initial page load.
    applyTheme(prefs.theme, { transition: false });
    // i18n languageChanged listener updates documentElement.dir/lang + localStorage.
    if (i18n.language !== prefs.language) void i18n.changeLanguage(prefs.language);
  }, [meQuery.data?.preferences, qc]);

  const value: AuthState = (() => {
    // Waiting for refresh round-trip or /me/ response.
    if (tokenAcquired === null || (tokenAcquired && meQuery.isPending)) {
      return { status: 'loading' };
    }
    // Refresh failed, or /me/ errored, or data missing.
    if (!tokenAcquired || meQuery.isError || !meQuery.data) {
      return { status: 'unauthenticated' };
    }
    return {
      status: 'authenticated',
      user: meQuery.data,
      isAdmin: meQuery.data.isStaff,
    };
  })();

  // Cross-tab logout sync — another tab calls useLogout which writes 'auth:logout'.
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key !== 'auth:logout') return;
      clearInMemoryToken();
      clearSessionHint();
      qc.clear();
      window.location.replace(withBase('/login'));
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [qc]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
